import { useCallback, useState, useEffect, useReducer } from 'react';
import { fetchFromServer } from './fetchFromServer';
import usePrevious from './usePrevious';

interface IAction<T> {
  type: 'fetchInit' | 'fetchSuccess' | 'fetchFailure' | 'fetchDone';
  payload?: {
    result?: T[];
    error?: any;
  };
}

interface IState<T> {
  isLoading: boolean;
  isError: boolean;
  data: T[];
  error: any;
  url?: string;
  status: 'idle' | 'pending' | 'rejected' | 'resolved';
}

const createFetchReducer = <T>() => (state: IState<T>, action: IAction<T>) => {
  switch (action.type) {
    case 'fetchInit':
      return { ...state, isLoading: true, isError: false, status: 'pending' };
    case 'fetchSuccess':
      return {
        ...state,
        isError: false,
        data: action.payload.result,
        status: 'resolved',
      };
    case 'fetchFailure':
      return {
        ...state,
        isError: true,
        error: action.payload.error,
        status: 'rejected',
      };
    case 'fetchDone':
      return {
        ...state,
        isLoading: false,
      };
    default:
      throw new Error();
  }
};

/**
 * @deprecated Use React Query instead. For more info, see here: https://bit.ly/3u0WlF7
 */
export const useFetchFromServer = <T = any>(initialState?: Partial<IState<T>>) => {
  const [url, setUrl] = useState<string>(initialState?.url ?? null);
  const [isRefetching, setIsRefetching] = useState(false);
  const prevUrl = usePrevious(url);

  const fetchReducer = createFetchReducer<T>();

  const [state, dispatch] = useReducer(fetchReducer, {
    isLoading: initialState && initialState.isLoading ? initialState.isLoading : false,
    isError: initialState && initialState.isError ? initialState.isError : false,
    data: initialState && initialState.data ? initialState.data : [],
    error: initialState && initialState.error ? initialState.error : null,
    status: initialState && initialState.status ? initialState.status : 'idle',
  });

  const refetch = useCallback(() => {
    setIsRefetching(true);
    setUrl(null);
  }, []);

  useEffect(() => {
    if (!url && isRefetching) {
      setUrl(prevUrl);
      setIsRefetching(false);
    }
  }, [isRefetching, prevUrl, url]);

  useEffect(() => {
    let didCancel = false;

    const fetchData = async () => {
      dispatch({ type: 'fetchInit' });

      fetchFromServer<T>(url)
        .then(result => {
          if (!didCancel) {
            dispatch({ type: 'fetchSuccess', payload: { result } });
          }
        })
        .catch(error => {
          if (!didCancel) {
            dispatch({ type: 'fetchFailure', payload: { error } });
          }
        })
        .then(() => {
          if (!didCancel) {
            dispatch({ type: 'fetchDone' });
          }
        });
    };

    if (url) {
      fetchData();
    }

    return () => {
      didCancel = true;
    };
  }, [url]);

  return { state, setUrl, refetch };
};
